import React from 'react';
import { X, Crown, Mail } from 'lucide-react';
import { salesLeadAPI } from '../../services/api.js';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  isDarkMode?: boolean;
}

const FREE_FEATURES = [
  { ok: true,  t: '3 AI agents' },
  { ok: true,  t: '50 agent runs / month' },
  { ok: true,  t: '1 profession pack' },
  { ok: true,  t: 'Basic memory (30 days)' },
  { ok: true,  t: 'Gmail + Twitter integration' },
  { ok: false, t: 'Heartbeat notifications' },
  { ok: false, t: 'WhatsApp / Telegram alerts' },
  { ok: false, t: 'Engram cross-session memory' },
];

const PRO_FEATURES = [
  { ok: true, t: 'Unlimited agents & runs' },
  { ok: true, t: 'All 6 profession packs' },
  { ok: true, t: 'Engram â€” persistent vector memory' },
  { ok: true, t: 'Agent heartbeat alerts' },
  { ok: true, t: 'WhatsApp + Telegram notifications' },
  { ok: true, t: 'All 50+ integrations' },
  { ok: true, t: 'AI Console â€” model switching & cost tracking' },
  { ok: true, t: '10K Gemini + 20K Groq credits / month' },
  { ok: true, t: 'BYOK â€” connect your own API keys' },
];

const TEAM_FEATURES = [
  { ok: true, t: 'Everything in Pro' },
  { ok: true, t: 'Shared agents across team' },
  { ok: true, t: 'Team Engram memory layer' },
  { ok: true, t: '150K AI tokens / seat / month' },
  { ok: true, t: 'Shared dashboards + admin controls' },
  { ok: true, t: 'Team AI budget management' },
  { ok: true, t: 'Priority support' },
  { ok: true, t: 'Custom agent workflows' },
];

const UpgradeModal: React.FC<UpgradeModalProps> = ({ isOpen, onClose, isDarkMode = false }) => {
  if (!isOpen) return null;

  const d = isDarkMode;

  const c = {
    accent:      'var(--app-primary-mid)',
    accentLight: 'var(--app-primary-soft)',
    accentMid:   'var(--app-primary-soft-2)',
    accentRing:  '#c8e6df',
    bg:          d ? 'var(--lyra-color-text)' : 'var(--lyra-color-surface-2)',
    surface:     d ? 'var(--lyra-color-text)' : 'var(--lyra-color-surface)',
    surface2:    d ? 'var(--lyra-color-text)' : 'var(--lyra-color-surface-2)',
    border:      d ? 'rgba(58,107,92,0.28)' : 'var(--lyra-color-border)',
    text:        d ? 'var(--app-surface-3)' : 'var(--lyra-color-text)',
    muted:       d ? '#8ab5a0' : 'var(--lyra-color-text-muted)',
    soft:        d ? '#5c8878' : '#94a3b8',
    checkBg:     d ? '#1a3d2a' : 'var(--lyra-color-success-bg)',
    checkColor:  'var(--lyra-color-success)',
    crossBg:     d ? 'var(--lyra-color-text)' : 'var(--lyra-color-danger-bg)',
    crossColor:  'var(--lyra-color-danger)',
    shadow:      '0 1px 3px rgba(0,0,0,.06), 0 0 0 1px rgba(0,0,0,.04)',
    shadow2:     '0 8px 32px rgba(58,107,92,.14), 0 2px 8px rgba(0,0,0,.08)',
    shadow3:     '0 24px 64px rgba(58,107,92,.18), 0 4px 16px rgba(0,0,0,.1)',
  };

  // No self-serve checkout exists yet — this is a lead-capture action, not
  // an instant upgrade. It's recorded server-side (so the team actually
  // sees it, not just whoever's inbox the mailto lands in) in addition to
  // opening the mailto, then a team member manually flips the user's plan
  // once a deal closes (see server/src/routes/admin.js).
  const handleProUpgrade = () => {
    salesLeadAPI.create('Pro plan interest — Upgrade modal').catch(() => {});
    window.open(
      'mailto:hello@agentlyra.in?subject=Pro%20Plan%20Upgrade&body=Hi%2C%20I%27d%20like%20to%20upgrade%20to%20the%20Pro%20plan.',
      '_blank'
    );
  };

  const handleTeamContact = () => {
    salesLeadAPI.create('Team plan interest — Upgrade modal').catch(() => {});
    window.open(
      'mailto:hello@agentlyra.in?subject=Team%20Plan%20Enquiry&body=Hi%2C%20I%27d%20like%20to%20learn%20more%20about%20the%20Team%20plan.',
      '_blank'
    );
  };

  const FeatureRow = ({ ok, t }: { ok: boolean; t: string }) => (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9, fontSize: 13, color: ok ? c.text : c.soft }}>
      <div style={{
        width: 17, height: 17, borderRadius: '50%',
        background: ok ? c.checkBg : c.crossBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, fontSize: 9, fontWeight: 700,
        color: ok ? c.checkColor : c.crossColor,
        marginTop: 1,
      }}>
        {ok ? 'âœ“' : 'âœ•'}
      </div>
      {t}
    </div>
  );

  const plans = [
    {
      tier: 'Free', price: '0', sub: '/mo',
      desc: 'For individuals getting started',
      features: FREE_FEATURES,
      featured: false,
      badge: 'Current plan',
      btnLabel: 'Current plan',
      btnDisabled: true,
      onBtn: undefined as undefined | (() => void),
    },
    {
      tier: 'Pro', price: '12', sub: '/mo',
      desc: 'For professionals running their work OS',
      features: PRO_FEATURES,
      featured: true,
      badge: 'Most Popular',
      btnLabel: 'Contact Sales',
      btnDisabled: false,
      onBtn: handleProUpgrade,
    },
    {
      tier: 'Team', price: '29', sub: '/seat/mo',
      desc: 'For teams sharing agents & memory',
      features: TEAM_FEATURES,
      featured: false,
      badge: null,
      btnLabel: 'Contact us',
      btnDisabled: false,
      onBtn: handleTeamContact,
    },
  ];

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
        background: 'rgba(10,18,14,0.65)',
        backdropFilter: 'blur(8px)',
        fontFamily: "'DM Sans', sans-serif",
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');`}</style>
      <div style={{
        position: 'relative',
        width: '100%', maxWidth: 860,
        maxHeight: '92vh', overflowY: 'auto',
        background: c.bg,
        borderRadius: 24,
        boxShadow: c.shadow3,
        border: `1px solid ${c.border}`,
      }}>

        {/* Header */}
        <div style={{
          padding: '28px 32px 20px',
          borderBottom: `1px solid ${c.border}`,
          position: 'relative',
        }}>
          <button
            onClick={onClose}
            style={{
              position: 'absolute', top: 16, right: 16,
              background: c.surface2, border: 'none', cursor: 'pointer',
              width: 30, height: 30, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: c.muted, fontSize: 14,
            }}
          >
            <X size={14} />
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
            <div style={{
              width: 38, height: 38, borderRadius: 12,
              background: `linear-gradient(135deg, ${c.accent}, var(--app-primary-deep))`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 6px 16px rgba(58,107,92,.35)',
            }}>
              <Crown size={18} color="var(--lyra-color-warning)" />
            </div>
            <div>
              <div style={{ fontSize: 20, fontWeight: 800, color: c.text, letterSpacing: -0.5 }}>Upgrade your plan</div>
              <div style={{ fontSize: 13, color: c.muted, marginTop: 1 }}>
                You're on the{' '}
                <span style={{ fontWeight: 700, color: c.text }}>Free plan</span>
                {' '}Â· Unlock the full Lyra experience
              </div>
            </div>
          </div>
        </div>

        {/* Plans */}
        <div style={{ padding: '24px 32px 28px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20, alignItems: 'start' }}>
          {plans.map((plan) => (
            <div
              key={plan.tier}
              style={{
                background: c.surface,
                border: plan.featured
                  ? `1.5px solid ${c.accent}`
                  : `1.5px solid ${c.border}`,
                borderRadius: 18,
                padding: 24,
                position: 'relative',
                boxShadow: plan.featured ? `0 0 0 4px ${c.accentRing}, ${c.shadow2}` : c.shadow,
                transform: plan.featured ? 'scale(1.025)' : 'none',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Badge */}
              {plan.featured && (
                <div style={{
                  position: 'absolute', top: -12, left: '50%',
                  transform: 'translateX(-50%)',
                  background: c.accent, color: 'var(--app-on-accent)',
                  fontSize: 10.5, fontWeight: 700,
                  padding: '4px 14px', borderRadius: 99,
                  whiteSpace: 'nowrap', letterSpacing: 0.3,
                }}>
                  Most Popular
                </div>
              )}
              {!plan.featured && plan.badge && (
                <div style={{
                  display: 'inline-block',
                  fontSize: 10.5, fontWeight: 700,
                  padding: '3px 10px', borderRadius: 99,
                  background: c.surface2, color: c.soft,
                  marginBottom: 8, alignSelf: 'flex-start',
                }}>
                  {plan.badge}
                </div>
              )}

              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: plan.featured ? c.accent : c.muted, marginBottom: 8, marginTop: plan.featured ? 6 : 0 }}>{plan.tier}</div>

              <div style={{ fontSize: 40, fontWeight: 800, color: c.text, lineHeight: 1, marginBottom: 4 }}>
                <sup style={{ fontSize: 18, fontWeight: 600, verticalAlign: 'super' }}>$</sup>
                {plan.price}
                <sub style={{ fontSize: 13, fontWeight: 400, color: c.muted }}>{plan.sub}</sub>
              </div>

              <div style={{ fontSize: 12.5, color: c.muted, marginBottom: 16, lineHeight: 1.45 }}>{plan.desc}</div>
              <div style={{ height: 1, background: c.border, marginBottom: 16 }} />

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, marginBottom: 20 }}>
                {plan.features.map((f) => <FeatureRow key={f.t} ok={f.ok} t={f.t} />)}
              </div>

              <button
                disabled={plan.btnDisabled}
                onClick={plan.onBtn}
                style={{
                  width: '100%', padding: '11px 0',
                  borderRadius: 11, fontSize: 13.5, fontWeight: 700,
                  cursor: plan.btnDisabled ? 'not-allowed' : 'pointer',
                  fontFamily: "'DM Sans', sans-serif",
                  transition: 'all .18s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                  ...(plan.btnDisabled
                    ? { background: c.surface2, color: c.soft, border: `1.5px solid ${c.border}` }
                    : plan.featured
                    ? { background: c.accent, color: 'var(--app-on-accent)', border: 'none', boxShadow: '0 4px 14px rgba(58,107,92,.35)' }
                    : { background: c.surface, color: c.text, border: `1.5px solid ${c.border}` }
                  ),
                }}
              >
                {plan.featured && <Crown size={14} color="var(--lyra-color-warning)" />}
                {(plan.tier === 'Pro' || plan.tier === 'Team') && <Mail size={14} />}
                {plan.btnLabel}
              </button>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', paddingBottom: 20, fontSize: 12.5, color: c.soft }}>
          Pro and Team aren't self-serve yet Â· "Contact Sales" reaches our team directly, no payment taken here
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
